import React, { useState, useEffect, useRef } from 'react';

/**
 * Animated number counter component with ease-out cubic animation
 * Triggers when scrolled into viewport
 */
export default function Counter({
  end,
  suffix = '',
  prefix = '',
  decimals = 0,
  duration = 1400
}) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const startAnimation = () => {
      if (animatedRef.current) return;
      animatedRef.current = true;
      const start = performance.now();
      const target = Number(end) || 0;

      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / duration);
        // Ease-out cubic formula
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        const currentVal = progress >= 1 ? target : easeProgress * target;
        setValue(currentVal);

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    };

    // If already in view on mount (e.g. Hero section above the fold)
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      startAnimation();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAnimation();
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 50px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  const formatted =
    decimals > 0
      ? value.toFixed(decimals)
      : Math.round(value)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return (
    <span
      ref={ref}
      className="counter-val"
      style={{
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: 'inherit',
        fontFamily: 'inherit',
        lineHeight: 'inherit',
        display: 'inline'
      }}
    >
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
